import { useQueries, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { Columns2, FlaskConical, RotateCcw, Settings2, X } from "lucide-react"
import * as React from "react"
import { useTranslation } from "react-i18next"
import type { AgentDetail } from "@/api/generated/AgentDetail"
import type { AgentSummary } from "@/api/generated/AgentSummary"
import { agentQuery } from "@/api/queries/agents"
import { useChatAgents, useCreateChatSession } from "@/api/queries/chat"
import { StatusBadge } from "@/components/app/status-badge"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Toggle } from "@/components/ui/toggle"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { AgentPicker, rememberedAgentId } from "@/features/chat/agent-picker"
import { Composer, type ComposerHandle } from "@/features/chat/composer"
import { Conversation } from "@/features/chat/conversation"
import { DropZone } from "@/features/chat/drop-zone"
import {
  overriddenKeys,
  type PlaygroundSettings,
  type SettingKey,
  toOverrides,
} from "@/features/chat/playground"
import { PlaygroundSettingsForm } from "@/features/chat/playground-settings"
import { toAttachmentRefs, useComposerHotkey } from "@/features/chat/session-chat"
import { isTerminal } from "@/features/chat/stream-reducer"
import { markRead, sendTurn, stopTurn, useChatStreams } from "@/features/chat/stream-store"
import { useAttachments } from "@/features/chat/use-attachments"
import { useSessionChat } from "@/features/chat/use-session-chat"
import { useIsMobile } from "@/hooks/use-mobile"
import { requireCap } from "@/lib/route-guards"
import { cn } from "@/lib/utils"

export const Route = createFileRoute("/_app/chat/playground")({
  beforeLoad: requireCap("management"),
  staticData: { titleKey: "chat.playground.title" },
  component: PlaygroundPage,
})

interface Pane {
  key: "a" | "b"
  agentId: number | null
  sessionId: number | null
  settings: PlaygroundSettings
}

const STORAGE_KEY = "mit.chat.playground"

function loadPanes(): Pane[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as Pane[]
      if (Array.isArray(parsed) && parsed.length > 0) return parsed.slice(0, 2)
    }
  } catch {
    // ignore unreadable state
  }
  return [{ key: "a", agentId: rememberedAgentId(), sessionId: null, settings: {} }]
}

const SUMMARY_LABEL: Record<SettingKey, string> = {
  system_prompt: "systemPrompt",
  model_id: "model",
  temperature: "temperature",
  top_p: "topP",
  top_k: "topK",
  thinking: "thinking",
  cache_static: "cacheStatic",
  cache_messages: "cacheMessages",
  mcp_server_ids: "mcp",
  skill_ids: "skills",
}

function PlaygroundPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const isMobile = useIsMobile()
  const agents = useChatAgents(true)
  const create = useCreateChatSession()
  const attachments = useAttachments()
  const composer = React.useRef<ComposerHandle>(null)
  useComposerHotkey(composer)
  const [panes, setPanes] = React.useState<Pane[]>(loadPanes)
  const [settingsFor, setSettingsFor] = React.useState<Pane["key"] | null>(null)

  React.useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(panes))
    } catch {
      // storage full / unavailable
    }
  }, [panes])

  // default agent once the list is known
  const firstAgent = agents.data?.[0]?.id ?? null
  React.useEffect(() => {
    if (!agents.data) return
    setPanes((ps) =>
      ps.map((p) =>
        p.agentId != null && agents.data.some((a) => a.id === p.agentId) ? p : { ...p, agentId: firstAgent },
      ),
    )
  }, [agents.data, firstAgent])

  // one query per distinct agent (both panes may use the same one)
  const agentIds = [...new Set(panes.map((p) => p.agentId).filter((id): id is number => id != null))]
  const details = useQueries({ queries: agentIds.map((id) => agentQuery(id)) })
  const agentOf = (i: number) => {
    const id = panes[i]?.agentId
    return id == null ? undefined : (details[agentIds.indexOf(id)]?.data as AgentDetail | undefined)
  }

  const live = useChatStreams((s) => s.live)
  const unread = useChatStreams((s) => s.unread)
  const busy = panes.some((p) => {
    const l = p.sessionId != null ? live[p.sessionId] : undefined
    return !!l && !isTerminal(l.turn.status)
  })
  // panes are always on screen: never mark their replies unread
  React.useEffect(() => {
    for (const p of panes) if (p.sessionId != null && unread[p.sessionId]) markRead(p.sessionId)
  }, [panes, unread])

  const update = (key: Pane["key"], patch: Partial<Pane>) =>
    setPanes((ps) => ps.map((p) => (p.key === key ? { ...p, ...patch } : p)))

  const compare = panes.length === 2
  const toggleCompare = () =>
    setPanes((ps) =>
      ps.length === 2
        ? [ps[0]!]
        : [...ps, { key: "b", agentId: ps[0]!.agentId, sessionId: null, settings: { ...ps[0]!.settings } }],
    )

  const send = async (text: string) => {
    const attachmentIds = attachments.ready.map((a) => a.upload!.id)
    const refs = toAttachmentRefs(attachments)
    try {
      await Promise.all(
        panes.map(async (p, i) => {
          if (p.agentId == null) return
          const agent = agents.data?.find((a) => a.id === p.agentId)
          let sessionId = p.sessionId
          if (sessionId == null) {
            const s = await create.mutateAsync({
              agent_id: p.agentId,
              title: t("chat.playground.sessionTitle", {
                agent: agent?.name ?? "",
                pane: p.key.toUpperCase(),
              }),
            })
            sessionId = s.id
            update(p.key, { sessionId })
          }
          void sendTurn({
            sessionId,
            request: {
              message: text,
              attachment_ids: attachmentIds,
              overrides: toOverrides(p.settings, agentOf(i)),
            },
            attachments: refs,
            qc,
          })
        }),
      )
      return true
    } catch {
      return false
    }
  }

  const stopAll = () => {
    for (const p of panes) if (p.sessionId != null) stopTurn(p.sessionId)
  }

  const editing = panes.find((p) => p.key === settingsFor)
  const editingIndex = panes.findIndex((p) => p.key === settingsFor)
  const inlineSettings = !compare && !isMobile

  return (
    <DropZone onFiles={attachments.add} className="flex h-full min-h-0 flex-col">
      <header className="flex h-12 shrink-0 items-center gap-2 border-b px-3 sm:px-4">
        <FlaskConical className="text-muted-foreground size-4" />
        <h1 className="truncate text-sm font-semibold">{t("chat.playground.title")}</h1>
        <span className="text-muted-foreground hidden truncate text-xs lg:inline">
          {t("chat.playground.description")}
        </span>
        <div className="ml-auto flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger
              render={
                <Toggle
                  size="sm"
                  variant="outline"
                  pressed={compare}
                  onPressedChange={toggleCompare}
                  aria-label={t("chat.playground.compare")}
                  data-testid="playground-compare"
                />
              }
            >
              <Columns2 />
              <span className="hidden sm:inline">{t("chat.playground.compare")}</span>
            </TooltipTrigger>
            <TooltipContent>{t("chat.playground.compareHint")}</TooltipContent>
          </Tooltip>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        {inlineSettings && panes[0] && (
          <aside className="bg-muted/20 w-80 shrink-0 overflow-y-auto border-r p-4 xl:w-96">
            <PlaygroundSettingsForm
              agent={agentOf(0)}
              settings={panes[0].settings}
              onChange={(settings) => update("a", { settings })}
            />
          </aside>
        )}
        <div
          className={cn(
            "grid min-h-0 min-w-0 flex-1",
            compare ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1",
          )}
        >
          {panes.map((p, i) => (
            <PlaygroundPane
              key={p.key}
              pane={p}
              agent={agentOf(i)}
              agents={agents.data ?? []}
              compare={compare}
              showSettingsButton={!inlineSettings}
              onAgentChange={(agentId) => update(p.key, { agentId, sessionId: null, settings: {} })}
              onClear={() => update(p.key, { sessionId: null })}
              onRemove={
                compare
                  ? () => setPanes((ps) => ps.filter((x) => x.key !== p.key).map((x) => ({ ...x, key: "a" })))
                  : undefined
              }
              onEditSettings={() => setSettingsFor(p.key)}
              className={cn(compare && i === 0 && "md:border-r", compare && "max-md:border-b")}
            />
          ))}
        </div>
      </div>

      <div className="shrink-0 border-t px-3 pt-3 pb-3 sm:px-6">
        <Composer
          ref={composer}
          draftKey="playground"
          className="mx-auto max-w-4xl"
          onSend={send}
          onStop={stopAll}
          streaming={busy}
          disabled={panes.every((p) => p.agentId == null)}
          attachments={attachments}
          placeholder={compare ? t("chat.playground.placeholderCompare") : t("chat.playground.placeholder")}
          showHint={false}
        />
      </div>

      <Sheet open={!!editing} onOpenChange={(o) => !o && setSettingsFor(null)}>
        <SheetContent className="w-full gap-0 sm:max-w-md">
          <SheetHeader className="border-b px-5 py-4">
            <SheetTitle>
              {compare && editing
                ? t("chat.playground.settingsFor", { pane: editing.key.toUpperCase() })
                : t("chat.playground.settings")}
            </SheetTitle>
            <SheetDescription>{t("chat.playground.settingsHint")}</SheetDescription>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
            {editing && (
              <PlaygroundSettingsForm
                agent={agentOf(editingIndex)}
                settings={editing.settings}
                onChange={(settings) => update(editing.key, { settings })}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </DropZone>
  )
}

function PlaygroundPane({
  pane,
  agent,
  agents,
  compare,
  showSettingsButton,
  onAgentChange,
  onClear,
  onRemove,
  onEditSettings,
  className,
}: {
  pane: Pane
  agent: AgentDetail | undefined
  agents: AgentSummary[]
  compare: boolean
  showSettingsButton: boolean
  onAgentChange: (id: number) => void
  onClear: () => void
  onRemove?: () => void
  onEditSettings: () => void
  className?: string
}) {
  const { t } = useTranslation()
  const overrides = React.useMemo(() => toOverrides(pane.settings, agent), [pane.settings, agent])
  const chat = useSessionChat(pane.sessionId, { overrides, viewing: false })
  const changed = overriddenKeys(pane.settings, agent)

  return (
    <section
      className={cn("flex min-h-0 min-w-0 flex-col", className)}
      aria-label={t("chat.playground.pane", { pane: pane.key.toUpperCase() })}
    >
      <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b px-3 py-2">
        {compare && (
          <span className="bg-primary/10 text-primary flex size-6 items-center justify-center rounded-md font-mono text-xs font-semibold">
            {pane.key.toUpperCase()}
          </span>
        )}
        <AgentPicker
          agents={agents}
          value={pane.agentId}
          onChange={onAgentChange}
          disabled={chat.busy}
          className="border-solid"
        />
        <div className="ml-auto flex items-center gap-0.5">
          {showSettingsButton && (
            <Button variant="ghost" size="sm" onClick={onEditSettings} data-testid="playground-edit-settings">
              <Settings2 />
              {t("chat.playground.settings")}
              {changed.length > 0 && (
                <StatusBadge tone="accent" className="ml-0.5 h-4 px-1">
                  {changed.length}
                </StatusBadge>
              )}
            </Button>
          )}
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="text-muted-foreground"
                  onClick={onClear}
                  disabled={chat.busy || pane.sessionId == null}
                  aria-label={t("chat.playground.clear")}
                />
              }
            >
              <RotateCcw />
            </TooltipTrigger>
            <TooltipContent>{t("chat.playground.clear")}</TooltipContent>
          </Tooltip>
          {onRemove && (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="text-muted-foreground"
                    onClick={onRemove}
                    disabled={chat.busy}
                    aria-label={t("chat.playground.removePane")}
                  />
                }
              >
                <X />
              </TooltipTrigger>
              <TooltipContent>{t("chat.playground.removePane")}</TooltipContent>
            </Tooltip>
          )}
        </div>
        {changed.length > 0 && (
          <div className="flex w-full flex-wrap gap-1">
            {changed.map((k) => (
              <StatusBadge key={k} tone="accent" className="font-normal">
                {t(`chat.playground.fields.${SUMMARY_LABEL[k]}` as "chat.playground.fields.model")}
              </StatusBadge>
            ))}
          </div>
        )}
      </div>
      <Conversation
        className="min-h-0 flex-1"
        contentClassName="max-w-none gap-6 px-4 sm:px-5"
        turns={chat.turns}
        agentName={agent?.name ?? ""}
        agentIcon={agent?.icon}
        busy={chat.busy}
        onRetry={(r) => chat.send(r)}
        empty={
          <div className="text-muted-foreground flex flex-1 flex-col items-center justify-center gap-2 py-16 text-center text-sm">
            <FlaskConical className="size-5 opacity-60" />
            <p>{t("chat.playground.empty")}</p>
          </div>
        }
      />
    </section>
  )
}
